/**
 * Triage actions that need a word from the user before the canonical
 * moves (CONTRACT §1): not relevant, walker got it wrong, duplicate,
 * snooze, accept risk. Runs `finding.transition`; a not-relevant or
 * walker-error dismissal also teaches the tester unless unticked.
 */
import { useState } from "react";
import type { FindingQueueQuery } from "@iamu/shared";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { resolveFindingTitle } from "@/lib/finding-resolution";
import { useTransitionFindings } from "./use-transition-findings";

type DismissAction =
  | "not_relevant"
  | "walker_error"
  | "out_of_scope"
  | "duplicate";

/** `dismiss` opens with a reason picker. */
export type TriageAction = DismissAction | "dismiss";

type GuidanceScopeOption = "project" | "outline" | "capability";

const DISMISS_OPTIONS: Array<{ value: DismissAction; label: string }> = [
  { value: "not_relevant", label: "Not relevant (by design)" },
  { value: "walker_error", label: "Walker got it wrong" },
  { value: "out_of_scope", label: "Handled outside system / Not in scope" },
  { value: "duplicate", label: "Duplicate of another finding" },
];

const SCOPE_OPTIONS: Array<{ value: GuidanceScopeOption; label: string }> = [
  { value: "project", label: "Whole project (default)" },
  { value: "outline", label: "This scenario outline only" },
  { value: "capability", label: "This capability only" },
];

const COPY: Record<
  Exclude<TriageAction, "dismiss">,
  { title: string; description: string; noteLabel: string; confirm: string }
> = {
  not_relevant: {
    title: "Not relevant",
    description:
      "It doesn't apply to your product by design. Your reason becomes a rule the walker follows next time.",
    noteLabel: "Why doesn't this apply? (optional)",
    confirm: "Dismiss",
  },
  walker_error: {
    title: "Walker got it wrong",
    description:
      "The walker misread the page. Dismissing hides this finding and adds a skip rule so future walkthroughs do not flag the same misread again.",
    noteLabel: "What did the walker misread? (optional)",
    confirm: "Dismiss as walker error",
  },
  out_of_scope: {
    title: "Handled outside system",
    description:
      "This process is handled out-of-band by humans. The walker will be instructed not to flag missing automation or alerts for it.",
    noteLabel: "How is this handled out of system? (optional)",
    confirm: "Dismiss as out of scope",
  },
  duplicate: {
    title: "Duplicate",
    description: "The same problem is already tracked as another finding.",
    noteLabel: "Note (optional)",
    confirm: "Mark duplicate",
  },
};

interface TriageActionDialogProps {
  action: TriageAction;
  /** Canonical ids to move (one, or a selection). */
  ids: string[];
  projectId: string;
  /** Shown under the title — usually the finding's title. */
  subject?: string;
  /** "Select all matching filter": the server resolves this instead of `ids`. */
  query?: FindingQueueQuery;
  onClose: () => void;
  /** After a successful move: the toast message and how many moved. */
  onDone?: (message: string, changed: number) => void;
}

export function TriageActionDialog({
  action: requested,
  ids,
  projectId,
  subject,
  query,
  onClose,
  onDone,
}: TriageActionDialogProps) {
  const [reason, setReason] = useState<DismissAction>("not_relevant");
  const action = requested === "dismiss" ? reason : requested;
  const copy = COPY[action];
  const [note, setNote] = useState("");
  const [teach, setTeach] = useState(true);
  const [rule, setRule] = useState("");
  const [scope, setScope] = useState<GuidanceScopeOption>("project");
  const [duplicateOf, setDuplicateOf] = useState("");
  const transition = useTransitionFindings(projectId);
  const { data: candidates } = trpc.finding.list.useQuery(
    { projectId, status: "open" },
    { enabled: action === "duplicate" },
  );

  const teaches =
    action === "not_relevant" ||
    action === "walker_error" ||
    action === "out_of_scope";
  const disabled =
    transition.isPending || (action === "duplicate" && !duplicateOf);

  function submit() {
    const trimmed = note.trim() || undefined;
    const trimmedRule = rule.trim() || undefined;
    const shared = ids.length > 1 || Boolean(query);
    const hasGuidanceOpts =
      Boolean(trimmedRule) || scope !== "project" || shared;
    const input = {
      to: "dismissed" as const,
      reason: action,
      note: trimmed,
      ...(action === "duplicate" ? { duplicateOf } : {}),
      ...(teaches && !teach
        ? { guidance: false as const }
        : teaches && hasGuidanceOpts
          ? {
              guidance: {
                ...(trimmedRule ? { rule: trimmedRule } : {}),
                ...(scope !== "project" ? { scope } : {}),
                ...(shared ? { shared: true } : {}),
              },
            }
          : {}),
    };
    const target = query ? { ids: [], query, projectId } : { ids };
    const message = `${copy.title}: done`;
    transition.run({ ...target, ...input }, message, (res) => {
      onDone?.(message, res.changed);
      onClose();
    });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* The dialog is portaled but still a React child of the finding row:
          keep typing and clicks from reaching the row's grid-list handlers
          (typeahead swallows keystrokes, a press opens the finding).
          Escape still reaches Radix to close the dialog. */}
      <DialogContent
        onKeyDown={(e) => {
          if (e.key !== "Escape") e.stopPropagation();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            {requested === "dismiss" ? "Dismiss with note" : copy.title}
          </DialogTitle>
          <DialogDescription>
            {subject ? `${subject}. ` : ""}
            {copy.description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {requested === "dismiss" && (
            <div className="space-y-2">
              <Label htmlFor="triage-dismiss-reason">Reason</Label>
              <Select
                value={reason}
                onValueChange={(v) => setReason(v as DismissAction)}
              >
                <SelectTrigger id="triage-dismiss-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DISMISS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {action === "duplicate" && (
            <div className="space-y-2">
              <Label htmlFor="triage-duplicate-of">Duplicate of</Label>
              <Select value={duplicateOf} onValueChange={setDuplicateOf}>
                <SelectTrigger id="triage-duplicate-of">
                  <SelectValue placeholder="Choose the finding it repeats" />
                </SelectTrigger>
                <SelectContent>
                  {(candidates ?? [])
                    .filter((c) => !ids.includes(c.id))
                    .map((c) => {
                      const title = resolveFindingTitle(c, c.id);
                      return (
                        <SelectItem key={c.id} value={c.id}>
                          {title}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="triage-note">{copy.noteLabel}</Label>
            <Textarea
              id="triage-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
          </div>
          {teaches && (
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="triage-teach"
                  checked={teach}
                  onCheckedChange={(v) => setTeach(v === true)}
                />
                <Label htmlFor="triage-teach" className="font-normal">
                  Teach the tester: add a rule so future walkthroughs skip this
                  {ids.length > 1 ? " (one rule for the selection)" : ""}
                </Label>
              </div>
              {teach && (
                <div className="grid gap-3 pl-6 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="triage-rule">Rule text (optional)</Label>
                    <Input
                      id="triage-rule"
                      value={rule}
                      onChange={(e) => setRule(e.target.value)}
                      placeholder={
                        subject
                          ? `Don't report: ${subject}`
                          : "Don't report this finding"
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="triage-scope">Rule scope</Label>
                    <Select
                      value={scope}
                      onValueChange={(v) => setScope(v as GuidanceScopeOption)}
                    >
                      <SelectTrigger id="triage-scope">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SCOPE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={disabled} onClick={submit}>
            {copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
